import { queryOptions } from '@tanstack/react-query';
import { GEOLOCATION_CONFIG, type GeocodeTier } from './config';
import { ElevationService } from './services/ElevationService';
import { NominatimService } from './services/NominatimService';
import type { AddressInput, GeoCoordinates } from './types/location';
import { coordinateCacheKey } from './utils/coordinates';
import { addressCacheKey, buildTierQuery } from './utils/geocodeQuery';

/**
 * Public geo endpoints must never be hammered, and Nominatim's usage policy
 * explicitly requires results to be cached client-side.
 *
 * Note the project `queryClient` retries only axios 502/503/504; a `fetch`
 * failure is not an `AxiosError`, so `retry: false` is belt-and-braces.
 */
const GEO_QUERY_DEFAULTS = {
  staleTime: Infinity,
  gcTime: GEOLOCATION_CONFIG.cache.gcTimeMs,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchOnMount: false,
} as const;

export const geoQueryKeys = {
  all: ['geo'] as const,
  geocodes: () => [...geoQueryKeys.all, 'geocode'] as const,
  /** `language` is part of the key because `accept-language` changes `display_name`. */
  geocode: (tier: GeocodeTier, address: AddressInput, language: string) =>
    [...geoQueryKeys.geocodes(), tier, addressCacheKey(tier, address), language] as const,
  elevations: () => [...geoQueryKeys.all, 'elevation'] as const,
  elevation: (coordinates: GeoCoordinates) =>
    [
      ...geoQueryKeys.elevations(),
      coordinateCacheKey(coordinates, GEOLOCATION_CONFIG.cache.elevationKeyDecimals),
    ] as const,
};

export const geoQueries = {
  geocode: (tier: GeocodeTier, address: AddressInput, language: string) =>
    queryOptions({
      queryKey: geoQueryKeys.geocode(tier, address, language),
      queryFn: ({ signal }) =>
        NominatimService.search(buildTierQuery(tier, address), language, signal),
      ...GEO_QUERY_DEFAULTS,
    }),

  elevation: (coordinates: GeoCoordinates) =>
    queryOptions({
      queryKey: geoQueryKeys.elevation(coordinates),
      queryFn: ({ signal }) => ElevationService.getElevation(coordinates, signal),
      ...GEO_QUERY_DEFAULTS,
    }),
};
